import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Usuario } from '../../types';
import usuarioService from '../../services/usuarioService';

interface ContadorUsuarios {
  total: number;
  comuns: number;
  admins: number;
  lideres: number;
  ativos: number;
  desativados: number;
}

export interface UsuarioEstatisticasRef {
  atualizarEstatisticas: () => Promise<void>;
}

interface UsuarioEstatisticasProps {
  atualizacao?: number; // Um contador que incrementa quando deve atualizar
}

const UsuarioEstatisticas = forwardRef<UsuarioEstatisticasRef, UsuarioEstatisticasProps>(({ atualizacao = 0 }, ref) => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string>('');
  const [contadores, setContadores] = useState<ContadorUsuarios>({
    total: 0,
    comuns: 0,
    admins: 0,
    lideres: 0,
    ativos: 0,
    desativados: 0
  });

  // Função para carregar usuários diretamente do serviço
  const carregarUsuarios = async () => {
    setCarregando(true);
    try {
      // Usando o método para obter dados completos dos usuários para administração
      const resultado = await usuarioService.obterTodosAdmin();
      if (resultado.success && resultado.usuarios) {
        setUsuarios(resultado.usuarios);
        calcularEstatisticas(resultado.usuarios);
      } else {
        setErro(resultado.message || 'Erro ao carregar usuários');
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setErro('Erro ao carregar usuários');
    } finally {
      setCarregando(false);
    }
  };

  // Função para calcular as estatísticas com base nos usuários
  const calcularEstatisticas = (listaUsuarios: Usuario[]) => {
    const novoContador: ContadorUsuarios = {
      total: listaUsuarios.length,
      comuns: listaUsuarios.filter((u: Usuario) => u.tipo === 'comum').length,
      admins: listaUsuarios.filter((u: Usuario) => u.tipo === 'admin').length,
      lideres: listaUsuarios.filter((u: Usuario) => u.ehLider === true || u.eh_lider === true).length,
      ativos: listaUsuarios.filter((u: Usuario) => u.ativo !== false).length,
      desativados: listaUsuarios.filter((u: Usuario) => u.ativo === false).length
    };
    
    // Log para depuração
    console.log('Estatísticas de usuários:', novoContador);
    console.log('Usuários carregados:', listaUsuarios);
    
    setContadores(novoContador);
  };

  // Expor o método de atualização para o componente pai
  useImperativeHandle(ref, () => ({
    atualizarEstatisticas: carregarUsuarios
  }));

  useEffect(() => {
    // Carregar usuários quando o componente for montado ou quando a prop de atualização mudar
    carregarUsuarios();
  }, [atualizacao]);

  if (carregando) {
    return <div className="p-4">Carregando estatísticas de usuários...</div>;
  }

  if (erro) {
    return <div className="p-4 text-red-600">Erro ao carregar estatísticas: {erro}</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-xl font-bold text-bravo-primary mb-4">Estatísticas de Usuários</h2>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
          <div className="text-3xl font-bold text-bravo-primary dark:text-white">{contadores.total}</div>
          <div className="text-sm text-bravo-secondary">Total de Usuários</div>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
          <div className="text-3xl font-bold text-purple-600">{contadores.admins}</div>
          <div className="text-sm text-bravo-secondary">Administradores</div>
        </div>
        
        <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
          <div className="text-3xl font-bold text-indigo-600">{contadores.comuns}</div>
          <div className="text-sm text-bravo-secondary">Usuários Comuns</div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg border border-green-100">
          <div className="text-3xl font-bold text-green-600">{contadores.lideres}</div>
          <div className="text-sm text-bravo-secondary">Líderes de Departamento</div>
        </div>
        
        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-100">
          <div className="text-3xl font-bold text-emerald-600">{contadores.ativos}</div>
          <div className="text-sm text-bravo-secondary">Usuários Ativos</div>
        </div>
        
        <div className="bg-red-50 p-4 rounded-lg border border-red-100">
          <div className="text-3xl font-bold text-red-600">{contadores.desativados}</div>
          <div className="text-sm text-bravo-secondary">Usuários Desativados</div>
        </div>
      </div>
    </div>
  );
});

export default UsuarioEstatisticas;
